import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';

import { PasswordField } from '../components/PasswordField';
import { AUTH_ROUTES } from '../routes';
import { signInSchema, type SignInFormValues } from '../schemas';

interface SignInStepProps {
  onSubmit: (values: SignInFormValues) => Promise<void>;
}

export function SignInStep({ onSubmit }: SignInStepProps): JSX.Element {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { username: '', password: '', rememberMe: true },
  });

  async function handleFormSubmit(values: SignInFormValues): Promise<void> {
    setSubmitError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      setSubmitError(getAuthErrorMessage(err));
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Stack spacing={2}>
        {submitError && <Alert severity="error">{submitError}</Alert>}

        <TextField
          label="Usuario"
          autoComplete="username"
          autoFocus
          fullWidth
          {...register('username')}
          error={!!errors.username}
          helperText={errors.username?.message}
        />

        <PasswordField
          label="Contraseña"
          autoComplete="current-password"
          fullWidth
          {...register('password')}
          error={!!errors.password}
          helperText={errors.password?.message}
        />

        <FormControlLabel
          control={<Checkbox defaultChecked {...register('rememberMe')} />}
          label="Recordarme"
        />

        <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
          {isSubmitting ? 'Ingresando...' : 'Ingresar'}
        </Button>

        <Link
          component={RouterLink}
          to={AUTH_ROUTES.FORGOT_PASSWORD}
          variant="body2"
          sx={{ textAlign: 'center' }}
        >
          ¿Olvidaste tu contraseña?
        </Link>
      </Stack>
    </form>
  );
}

function getAuthErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    const name = err.name;
    switch (name) {
      case 'NotAuthorizedException':
      case 'UserNotFoundException':
        return 'Usuario o contraseña incorrectos';
      case 'UserNotConfirmedException':
        return 'La cuenta no está confirmada. Contactá al administrador.';
      case 'PasswordResetRequiredException':
        return 'Es necesario restablecer la contraseña.';
      default:
        return err.message || 'Error al ingresar. Intentá nuevamente.';
    }
  }
  return 'Error al ingresar. Intentá nuevamente.';
}
